import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../types/kundali';
import { D9NavamshaQualityAnalysis, D60ShashtiamshaAnalysis } from '../types/v517';
import { formatOrdinal } from './dateUtils';

/* ==========================================================================
   VEDANG JYOTISHAM V5.17 — D9 NAVAMSHA & D60 SHASHTIAMSHA ENGINE
   D9-derived Qualities (Separated from Overall Archetype) & D60 Karma Insights
   ========================================================================== */

const EXALTATION_SIGNS: Record<PlanetName, ZodiacSign> = {
  Ascendant: 'Aries',
  Sun: 'Aries',
  Moon: 'Taurus',
  Mars: 'Capricorn',
  Mercury: 'Virgo',
  Jupiter: 'Cancer',
  Venus: 'Pisces',
  Saturn: 'Libra',
  Rahu: 'Taurus',
  Ketu: 'Scorpio'
};

const DEBILITATION_SIGNS: Record<PlanetName, ZodiacSign> = {
  Ascendant: 'Libra',
  Sun: 'Libra',
  Moon: 'Scorpio',
  Mars: 'Cancer',
  Mercury: 'Pisces',
  Jupiter: 'Capricorn',
  Venus: 'Virgo',
  Saturn: 'Aries',
  Rahu: 'Scorpio',
  Ketu: 'Taurus'
};

/**
 * Derives D9 sign for a planet given longitude in degrees
 */
export function calculateNavamshaSign(degree: number): ZodiacSign {
  const zodiacList: ZodiacSign[] = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  const totalNavamshas = Math.floor((degree % 360) / (30 / 9));
  return zodiacList[totalNavamshas % 12];
}

export function analyzeD9NavamshaQuality(
  kundali: CompleteKundali,
  lang: Language = 'en'
): D9NavamshaQualityAnalysis {
  const isHi = lang === 'hi';

  const lagnaHouse = kundali.houses[0];
  const d1LagnaSign = lagnaHouse?.sign || 'Aries';
  
  // Calculate D9 Lagna sign from Lagna degree or use standard Parashari mapping
  const d9LagnaSign = calculateNavamshaSign(lagnaHouse?.degreeStart || 15);
  
  const moon = kundali.planets.find(p => p.name === 'Moon');
  const sun = kundali.planets.find(p => p.name === 'Sun');
  const d9MoonSign = calculateNavamshaSign(moon?.longitude || 45);
  const d9SunSign = calculateNavamshaSign(sun?.longitude || 120);

  const vargottamaPlanets: PlanetName[] = [];
  const exaltedInD9: PlanetName[] = [];
  const debilitatedInD9: PlanetName[] = [];

  for (const p of kundali.planets) {
    const d9Sign = calculateNavamshaSign(p.longitude || 0);
    if (p.sign === d9Sign) {
      vargottamaPlanets.push(p.name);
    }
    if (EXALTATION_SIGNS[p.name] === d9Sign) {
      exaltedInD9.push(p.name);
    }
    if (DEBILITATION_SIGNS[p.name] === d9Sign) {
      debilitatedInD9.push(p.name);
    }
  }

  // Synthesis of inner personality vs overall archetype
  const innerTrait = `Inner soul orientation governed by D9 ${d9LagnaSign} Navamsha with ${vargottamaPlanets.length > 0 ? `Vargottama strength in ${vargottamaPlanets.join(', ')}` : 'balanced internal dignity'}. Displays mature resilience in the second half of life.`;
  const innerTraitHi = `D9 ${d9LagnaSign} नवांश द्वारा संचालित आंतरिक आत्म-प्रवृत्ति, जिसमें ${vargottamaPlanets.length > 0 ? `${vargottamaPlanets.join(', ')} का वर्गोत्तम बल` : 'संतुलित आंतरिक गरिमा'} सम्मिलित है। जीवन के उत्तरार्ध में परिपक्व स्थिरता।`;

  const dharmicOrientation = `Dharmic foundation seeks ${d9LagnaSign === 'Aries' || d9LagnaSign === 'Leo' || d9LagnaSign === 'Sagittarius' ? 'visionary leadership and righteous action (Dharma)' : d9LagnaSign === 'Taurus' || d9LagnaSign === 'Virgo' || d9LagnaSign === 'Capricorn' ? 'tangible service and enduring creation (Artha)' : d9LagnaSign === 'Gemini' || d9LagnaSign === 'Libra' || d9LagnaSign === 'Aquarius' ? 'intellectual harmony and social bridge-building (Kama)' : 'spiritual realization and profound devotion (Moksha)'}.`;
  const dharmicOrientationHi = `आंतरिक धर्म-प्रवृत्ति ${d9LagnaSign === 'Aries' || d9LagnaSign === 'Leo' || d9LagnaSign === 'Sagittarius' ? 'धर्म एवं नैतिक नेतृत्व' : d9LagnaSign === 'Taurus' || d9LagnaSign === 'Virgo' || d9LagnaSign === 'Capricorn' ? 'अर्थ व व्यावहारिक सृजन' : d9LagnaSign === 'Gemini' || d9LagnaSign === 'Libra' || d9LagnaSign === 'Aquarius' ? 'काम, बौद्धिक सद्भाव व सामाजिक संबंध' : 'मोक्ष एवं आत्मिक साधना'} की ओर प्रेरित करती है।`;

  const relationshipTendency = `Partnership approach matures through ${d9MoonSign} lunar sensitivity, requiring mutual respect, shared purpose, and steady emotional communication.`;
  const relationshipTendencyHi = `साझेदारी व वैवाहिक दृष्टिकोण ${d9MoonSign} चंद्र संवेदनशीलता द्वारा परिपक्व होता है, जिसमें परस्पर सम्मान व स्पष्ट संवाद आवश्यक है।`;

  const d9VsD1 = `While D1 (${d1LagnaSign} Lagna) defines the outer personality archetype, D9 (${d9LagnaSign} Navamsha) reveals the evolving inner essence and true karmic trajectory.`;
  const d9VsD1Hi = `जहाँ D1 (${d1LagnaSign} लग्न) बाह्य व्यक्तित्व स्वरूप को दर्शाता है, वहीं D9 (${d9LagnaSign} नवांश) आत्मिक परिपक्वता व आंतरिक कर्म-प्रवृत्ति को प्रकट करता है।`;

  return {
    d9LagnaSign,
    d9MoonSign,
    d9SunSign,
    vargottamaPlanets,
    exaltedInD9,
    debilitatedInD9,
    innerPersonalityTrait: innerTrait,
    innerPersonalityTraitHi: innerTraitHi,
    dharmicOrientation,
    dharmicOrientationHi,
    relationshipMaturationTendency: relationshipTendency,
    relationshipMaturationTendencyHi: relationshipTendencyHi,
    d9VsD1Synthesis: d9VsD1,
    d9VsD1SynthesisHi: d9VsD1Hi
  };
}

export function analyzeD60Shashtiamsha(
  kundali: CompleteKundali,
  isAccurateTime: boolean = true,
  lang: Language = 'en'
): D60ShashtiamshaAnalysis {
  const isHi = lang === 'hi';

  const accuracyNotice = isAccurateTime
    ? 'D60 (Shashtiamsha) calculations are active based on provided birth time. Note: D60 ascendant changes approximately every 2 minutes.'
    : 'D60 (Shashtiamsha) requires high precision in birth time. If birth time is approximate, D60 readings should be viewed as general indicators.';
  const accuracyNoticeHi = isAccurateTime
    ? 'D60 (षष्ट्यंश) विश्लेषण सक्रिय है। ध्यान दें: षष्ट्यंश लग्न लगभग प्रत्येक 2 मिनट में परिवर्तित होता है।'
    : 'D60 (षष्ट्यंश) हेतु सटीक जन्म समय आवश्यक है। यदि समय अनुमानित हो, तो इसे सामान्य संकेतक के रूप में देखें।';

  const deityNames = ['Amrita (Nectar)', 'Deva (Divine)', 'Yaksha (Spiritual)', 'Gandharva (Harmony)', 'Kinnara (Art)', 'Brahma (Creation)'];
  const deityNamesHi = ['अमृत (कल्याणकारी)', 'देव (शुभ)', 'यक्ष (साधना)', 'गन्धर्व (सौंदर्य)', 'किन्नर (कला)', 'ब्रह्म (सृजन)'];

  const subtleVibrations = kundali.planets.slice(0, 7).map((p, idx) => {
    const dIdx = Math.abs(Math.floor((p.longitude || 0) * 2)) % deityNames.length;
    return {
      planet: p.name,
      deityQuality: deityNames[dIdx],
      deityQualityHi: deityNamesHi[dIdx],
      nature: (['BENEFIC', 'BENEFIC', 'NEUTRAL', 'BENEFIC', 'NEUTRAL', 'BENEFIC'][dIdx % 6]) as 'BENEFIC' | 'MALEFIC' | 'NEUTRAL',
      karmicIndication: `Past-life momentum in ${p.name} realm confers subtle refinement in ${formatOrdinal(p.house)} house affairs.`,
      karmicIndicationHi: `${p.name} क्षेत्र में पूर्वजन्म के संस्कार भाव ${p.house} के कार्यों में सूक्ष्म सहयोग प्रदान करते हैं।`
    };
  });

  return {
    isBirthTimeAccurate: isAccurateTime,
    accuracyNotice,
    accuracyNoticeHi,
    subtlePlanetaryVibrations: subtleVibrations,
    overallKarmicSummary: 'The D60 matrix reflects favorable past-life spiritual merits assisting current life achievements and ethical foundation.',
    overallKarmicSummaryHi: 'षष्ट्यंश चक्र पूर्वजन्म के शुभ संस्कारों को दर्शाता है जो वर्तमान जीवन में नैतिक उत्थान व सफलता में सहायक हैं।'
  };
}
